import type { Message, Part } from "@mux/sdk/v2/client"
import { indexedDBSource } from "./browser"

const DATABASE = "mux-starter-pages"
const VERSION = 1
const STORE = "pages"

/** The page of a chat's transcript that ends at a starter message, in the source chat's IDs. */
export type StarterPage = {
  sessionID: string
  messageID: string
  items: Array<{ info: Message; parts: Part[] }>
}

type StoredStarterPage = StarterPage & { key: string; server: string }

const pageKey = (server: string, sessionID: string, messageID: string) => JSON.stringify([server, sessionID, messageID])

function requestResult<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"))
  })
}

function transactionDone(transaction: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"))
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"))
  })
}

function validPage(value: unknown): value is StoredStarterPage {
  if (!value || typeof value !== "object") return false
  const page = value as Partial<StoredStarterPage>
  return (
    typeof page.server === "string" &&
    typeof page.sessionID === "string" &&
    typeof page.messageID === "string" &&
    Array.isArray(page.items) &&
    page.items.every((item) => !!item && typeof item === "object" && !!item.info && Array.isArray(item.parts))
  )
}

/**
 * Disposable cache of the pages starter messages fork with, per machine. A
 * starter's history does not change, so a page stays usable until its starter
 * is removed; the machine's copy of a fork remains authoritative.
 */
export function createStarterPageRepository(input?: { indexedDB?: IDBFactory }) {
  const source = input?.indexedDB ?? indexedDBSource()
  let opening: Promise<IDBDatabase | undefined> | undefined
  let writing = Promise.resolve()
  let disposed = false

  const database = () => {
    if (disposed || !source) return Promise.resolve(undefined)
    opening ??= new Promise<IDBDatabase | undefined>((resolve) => {
      const request = source.open(DATABASE, VERSION)
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE, { keyPath: "key" }).createIndex("server", "server")
      }
      request.onsuccess = () => {
        const result = request.result
        result.onversionchange = () => result.close()
        resolve(result)
      }
      request.onerror = () => resolve(undefined)
      request.onblocked = () => resolve(undefined)
    })
    return opening
  }

  // Writes run one at a time so a removal never races the write it follows.
  const mutate = (change: (store: IDBObjectStore) => void) => {
    if (disposed) return Promise.resolve()
    const request = writing.then(async () => {
      const db = await database()
      if (!db) return
      try {
        const transaction = db.transaction(STORE, "readwrite")
        const done = transactionDone(transaction)
        change(transaction.objectStore(STORE))
        await done
      } catch {
        // A cache write is an optimization; the machine still serves the page.
      }
    })
    writing = request
    return request
  }

  return {
    /** Every page cached for `server`. */
    async list(server: string): Promise<StarterPage[]> {
      await writing
      const db = await database()
      if (!db || disposed) return []
      try {
        const values = await requestResult<unknown[]>(
          db.transaction(STORE, "readonly").objectStore(STORE).index("server").getAll(server),
        )
        return values.filter(validPage).map(({ sessionID, messageID, items }) => ({ sessionID, messageID, items }))
      } catch {
        return []
      }
    },
    /** Stores a plain (structured-cloneable) page. */
    write(server: string, page: StarterPage) {
      return mutate((store) => {
        store.put({ ...page, server, key: pageKey(server, page.sessionID, page.messageID) } satisfies StoredStarterPage)
      })
    },
    remove(server: string, sessionID: string, messageID: string) {
      return mutate((store) => {
        store.delete(pageKey(server, sessionID, messageID))
      })
    },
    dispose() {
      disposed = true
      void writing.then(() => opening?.then((db) => db?.close()))
    },
  }
}

export type StarterPageRepository = Pick<ReturnType<typeof createStarterPageRepository>, "list" | "write" | "remove">
